<template>
  <v-card class="ma-6">
    <v-card-title>
      <v-icon color="primary" class="mr-6">mdi-api</v-icon>
      API Calls for Snapshot {{ snapshotId }}
      <v-btn small text color="primary" class="ml-4" :to="{ name: 'Snapshot', params: { id: snapshotId } }">
        <v-icon left small>mdi-arrow-left</v-icon> Back to Snapshot
      </v-btn>
      <div class="flex-grow-1"></div>
      <v-text-field color="green" v-model="search" append-icon="search" label="Search request or API name" single-line
        hide-details></v-text-field>
    </v-card-title>

    <v-data-table dense :headers="headers" :options.sync="options" :items="items"
      :server-items-length="totalItems" :footer-props="{
        itemsPerPageOptions: [10, 20, 50, 100, 500, -1]
      }" loading-text="Loading...please wait" :loading="loading">

      <template v-slot:item.requestDateTime="{ item }">
        {{ formatDateTime(item.requestDateTime) }}
      </template>

      <template v-slot:item.responseDateTime="{ item }">
        {{ formatDateTime(item.responseDateTime) }}
      </template>

      <template v-slot:item.request="{ item }">
        <div class="truncated-text" :title="item.request">{{ item.request }}</div>
      </template>

      <template v-slot:item.responseSnippet="{ item }">
        <div class="truncated-text">{{ item.responseSnippet }}</div>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn small icon @click="showApiCall(item)" title="View full response">
          <v-icon small>mdi-open-in-new</v-icon>
        </v-btn>
      </template>

    </v-data-table>

    <v-dialog v-model="dialog" max-width="1600px" scrollable>
      <v-card>
        <v-card-title>
          <v-icon color="primary" class="mr-3">mdi-api</v-icon>
          API Call details
          <div class="flex-grow-1"></div>
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle v-if="selectedItem">
          {{ selectedItem.apiName }} &mdash;
          Requested {{ formatDateTime(selectedItem.requestDateTime) }},
          Responded {{ formatDateTime(selectedItem.responseDateTime) }}
        </v-card-subtitle>
        <v-card-text>
          <div class="subtitle-2 mb-1">Request</div>
          <pre class="detail-pre mb-4">{{ selectedItem ? selectedItem.request : '' }}</pre>
          <div class="subtitle-2 mb-1">
            Response
            <span v-if="responseIsJson" class="caption grey--text">(formatted JSON)</span>
          </div>
          <v-progress-linear v-if="detailLoading" indeterminate color="primary"></v-progress-linear>
          <pre v-else class="detail-pre">{{ formattedResponse }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { SnapshotsService } from '../services/api/snapshots';
import _ from "lodash";
import moment from "moment";

const apiSnapshots = new SnapshotsService();

export default {
  data() {
    return {
      snapshotId: this.$route.params.id,
      totalItems: 0,
      items: [],
      loading: true,
      options: {},
      search: '',
      dialog: false,
      detailLoading: false,
      selectedItem: null,
      formattedResponse: '',
      responseIsJson: false,

      headers: [
        { text: 'ID', value: 'id', align: ' d-none' },
        { text: 'Request Date / Time', value: 'requestDateTime' },
        { text: 'Response Date / Time', value: 'responseDateTime' },
        { text: 'API', value: 'apiName' },
        { text: 'Request', value: 'request' },
        { text: 'Response', value: 'responseSnippet', sortable: false },
        { text: 'Response Length', value: 'responseLength', align: 'end' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
    }
  },

  watch: {
    options: {
      handler() {
        this.loadItems()
      },
      deep: true,
    },
    search: function () {
      this.debouncedloadItems();
    },
  },

  created: function () {
    this.debouncedloadItems = _.debounce(this.loadItems, 500);
  },

  methods: {
    formatDateTime(value) {
      if (!value) {
        return '';
      }
      return moment(value).format('MM/DD/YY h:mm:ss A');
    },

    formatResponse(response) {
      if (response == null) {
        return { text: '', isJson: false };
      }
      try {
        return { text: JSON.stringify(JSON.parse(response), null, 2), isJson: true };
      } catch (e) {
        return { text: response, isJson: false };
      }
    },

    showApiCall(item) {
      this.selectedItem = item;
      this.formattedResponse = '';
      this.responseIsJson = false;
      this.detailLoading = true;
      this.dialog = true;

      apiSnapshots
        .getSnapshotApiCall(this.snapshotId, item.id)
        .then((data) => {
          const { text, isJson } = this.formatResponse(data.response);
          this.formattedResponse = text;
          this.responseIsJson = isJson;
        })
        .finally(() => {
          this.detailLoading = false;
        });
    },

    loadItems() {
      this.loading = true;
      this.items = [];

      const search = {
        searchText: this.search,
        sortOrderField: this.options.sortBy.length == 1 ? this.options.sortBy[0] : "RequestDateTime",
        sortDirection: this.options.sortDesc.length == 0 ? "asc" : (this.options.sortDesc[0] ? "desc" : "asc"),
        itemsPerPage: this.options.itemsPerPage,
        itemsToSkip: this.options.itemsPerPage * (this.options.page - 1),
      };

      apiSnapshots
        .getSnapshotApiCalls(this.snapshotId, search)
        .then((data) => {
          this.items = data.items;
          this.totalItems = data.totalItemCount;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
}
</script>

<style scoped>
.truncated-text {
  max-width: 500px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.detail-pre {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: "Consolas", "Courier New", monospace;
  font-size: 13px;
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  margin: 0;
}
</style>
